import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseGrid from 'components/atoms/base-grid';
import { useFormik } from 'formik';
import BaseInput from 'components/atoms/base-input';
import ClinicInvoiceTermSelectBox from 'components/molecules/clinic-invoice-terms-select-box';
import CustomerTypeSelectBox from 'components/molecules/customer-type-select-box';
import BaseButton from 'components/atoms/baseButton';
import { createPortal } from 'react-dom';

import { validationSchema } from './validation-schema';

import type { IClinicInvoiceInfo } from 'infrastructure/interfaces';

interface IProps {
  data: IClinicInvoiceInfo;
  isDisabled?: boolean;
  onSave: (data: IClinicInvoiceInfo) => void;
}

const ClinicInvoicesInfoEditForm: React.FC<IProps> = (props) => {
  const { t } = useTranslation();

  const { data, isDisabled, onSave } = props;

  const [controlsContentEl, setControlsContentEl] =
    useState<HTMLElement | null>(null);

  const [customerTypeName, setCustomerTypeName] = useState('');

  const npiIsRequired = customerTypeName.trim().toLowerCase() !== 'no invoice';

  const formik = useFormik<IClinicInvoiceInfo>({
    initialValues: structuredClone(data),
    onSubmit: async (values) => {
      onSave(values);
    },
    validationSchema: validationSchema({ npiIsRequired }),
    validateOnChange: false,
  });

  useEffect(() => {
    formik.setValues(structuredClone(data));
  }, [data]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const element = document.getElementById('invoice-form-control');
      setControlsContentEl(element);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div>
      {controlsContentEl &&
        createPortal(
          <>
            <BaseButton
              dataCy="saveInvoice-button"
              width="160px"
              onClick={formik.submitForm}
              label={t('controls.save')}
              disabled={isDisabled}
            />
          </>,
          controlsContentEl,
        )}
      <BaseForm formik={formik}>
        <BaseGrid columns={2}>
          <BaseFormItem
            required
            name="invoiceTerms"
            label={t('labels.invoiceTerms')}
          >
            <ClinicInvoiceTermSelectBox />
          </BaseFormItem>
          <BaseFormItem
            required
            name="invoiceFolder"
            label={t('labels.folderName')}
          >
            <BaseInput />
          </BaseFormItem>
          <BaseFormItem
            required
            name="customerTypeGuid"
            label={t('labels.customerType')}
          >
            <CustomerTypeSelectBox
              onChange={(v, o) => {
                formik.setFieldValue('customerTypeGuid', v);
                setCustomerTypeName(o.label as string);
              }}
              onLoaded={(v, o) => {
                if (o) setCustomerTypeName(o.label as string);
              }}
            />
          </BaseFormItem>
          <BaseFormItem
            required={npiIsRequired}
            name="invoiceNPI"
            label={t('labels.invoiceNpi')}
          >
            <BaseInput />
          </BaseFormItem>
        </BaseGrid>
      </BaseForm>
    </div>
  );
};

export default ClinicInvoicesInfoEditForm;
